import { useEffect, useState } from "react";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { Mail } from "lucide-react";
import { YMark } from "@/components/Brand";
import { useTheme } from "@/context/ThemeContext";

/**
 * AuthOptions — web port of iOS `src/app/(auth)/options.tsx`.
 *
 * The iOS version is presented as `formSheet` at detent 0.62, i.e. a
 * dismissible sheet that covers ~62% of the screen from the bottom, with
 * a system-drawn grabber at the top and the landing screen dimmed behind.
 *
 * On web we reproduce that exact behaviour:
 *   • Full-screen dim backdrop (click to dismiss → navigate back to /welcome)
 *   • Sheet card:
 *       - mobile: pinned to the bottom with a top-round radius, slides up
 *       - desktop: centered card with all-round radius
 *   • Grabber pill at the top of the sheet
 *   • Content is 1:1 with iOS options.tsx — same padding, same fonts,
 *     same Y mark size, same title/subtitle copy, same three pill buttons
 *     ("Continue with Apple", "Continue with Google", "Continue with Email"),
 *     icon pinned at left:22, label centered.
 *
 * Apple/Google are non-functional per the app owner's instruction — the web
 * OAuth endpoints are still being prepared. Email routes into the existing
 * signup/login screens so email flows are unaffected.
 */

// Exact copy from iOS `options.tsx`.
const COPY = {
  signup: {
    title: "Create your account",
    subtitle: "Join BrontoY in seconds.",
  },
  login: {
    title: "Welcome back",
    subtitle: "Log in to continue on BrontoY.",
  },
};

// Apple logo — SVG path matches iOS `options.tsx` AppleLogo component.
function AppleLogo({ size = 22, color = "#fff" }) {
  return (
    <svg width={size} height={size} viewBox="0 0 814 1000" aria-hidden="true">
      <path
        fill={color}
        d="M788.1 340.9c-5.8 4.5-108.2 62.2-108.2 190.5 0 148.4 130.3 200.9 134.2 202.2-.6 3.2-20.7 71.9-68.7 141.9-42.8 61.6-87.5 123.1-155.5 123.1s-85.5-39.5-164-39.5c-76.5 0-103.7 40.8-165.9 40.8s-105.6-57-155.5-127C46.7 790.7 0 663 0 541.8c0-194.4 126.4-297.5 250.8-297.5 66.1 0 121.2 43.4 162.7 43.4 39.5 0 101.1-46 176.3-46 28.5 0 130.9 2.6 198.3 99.2zm-234-181.5c31.1-36.9 53.1-88.1 53.1-139.3 0-7.1-.6-14.3-1.9-20.1-50.6 1.9-110.8 33.7-147.1 75.8-28.5 32.4-55.1 83.6-55.1 135.5 0 7.8 1.3 15.6 1.9 18.1 3.2.6 8.4 1.3 13.6 1.3 45.4 0 102.5-30.4 135.5-71.3z"
      />
    </svg>
  );
}

// Google "G" in its four brand colours — SVG matches iOS GoogleLogo component.
function GoogleLogo({ size = 21 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 48 48" aria-hidden="true">
      <path
        fill="#EA4335"
        d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"
      />
      <path
        fill="#4285F4"
        d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"
      />
      <path
        fill="#FBBC05"
        d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"
      />
      <path
        fill="#34A853"
        d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"
      />
    </svg>
  );
}

export default function AuthOptions() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { theme } = useTheme();
  const isDark = theme === "dark";

  const mode = params.get("mode") === "login" ? "login" : "signup";
  const isSignup = mode === "signup";
  const { title, subtitle } = COPY[mode];

  // Show notice when Apple/Google are tapped — endpoints not wired on web yet.
  const [notice, setNotice] = useState("");

  const dismiss = () => {
    // Prefer going back so the landing scroll position is preserved.
    if (window.history.length > 1 && location.key !== "default") {
      navigate(-1);
    } else {
      navigate("/welcome", { replace: true });
    }
  };

  // Close on Escape — mirrors iOS "swipe down to dismiss".
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") dismiss();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const notReady = (which) => {
    setNotice(
      `${which} sign-in is arriving on web shortly. Please continue with Email for now.`
    );
  };

  const goEmail = () => {
    // Small delay to let the sheet's exit animation feel natural before the
    // next screen mounts, matching the iOS transition (~340ms).
    navigate(isSignup ? "/signup" : "/login");
  };

  // Secondary-button surface (Google + Email). iOS uses theme.secondaryBg
  // (#EFEFEF light / #262626 dark), rendered here via the `bg-secondary`
  // Tailwind token which resolves through our HSL vars.
  const secondaryBtnClass =
    "relative h-14 rounded-full flex items-center justify-center bg-secondary transition-all active:scale-[0.99] active:opacity-90 hover:opacity-95 disabled:opacity-70";

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/45 sm:bg-black/50 backdrop-blur-[2px]"
      onClick={dismiss}
      data-testid="auth-options-backdrop"
      role="dialog"
      aria-modal="true"
    >
      <div
        className="w-full sm:max-w-[440px] bg-background text-foreground rounded-t-[24px] sm:rounded-[24px] shadow-2xl px-6 pt-3 pb-6 sm:mb-0 anim-fade-up"
        style={{
          animationDuration: "0.32s",
          paddingBottom: "max(24px, env(safe-area-inset-bottom))",
        }}
        onClick={(e) => e.stopPropagation()}
        data-testid="auth-options-sheet"
      >
        {/* Grabber — matches iOS sheetGrabberVisible: true */}
        <button
          type="button"
          onClick={dismiss}
          className="mx-auto block w-9 h-[5px] rounded-full bg-muted-foreground/35 hover:bg-muted-foreground/55 transition-colors mb-3"
          aria-label="Dismiss"
          data-testid="auth-options-grabber"
        />

        {/* Header — Y mark 40, title 24 weight 800, subtitle 15 */}
        <div className="flex flex-col items-center mt-1.5">
          <YMark height={40} testId="auth-options-y-mark" />
          <h1
            className="font-jakarta font-extrabold text-center text-foreground"
            style={{
              fontSize: 24,
              letterSpacing: "-0.5px",
              marginTop: 16,
              lineHeight: 1.15,
            }}
            data-testid="auth-options-title"
          >
            {title}
          </h1>
          <p
            className="text-muted-foreground text-center"
            style={{ fontSize: 15, marginTop: 6, lineHeight: 1.35 }}
            data-testid="auth-options-subtitle"
          >
            {subtitle}
          </p>
        </div>

        {/* Options — gap 12, marginTop 26.
            Icon absolutely positioned at left:22, label horizontally centered. */}
        <div className="flex flex-col gap-3" style={{ marginTop: 26 }}>
          {/* Apple — black in light mode, white in dark mode */}
          <button
            type="button"
            onClick={() => notReady("Apple")}
            className="relative h-14 rounded-full flex items-center justify-center transition-all active:scale-[0.99] active:opacity-90 hover:opacity-95"
            style={{
              backgroundColor: isDark ? "#FFFFFF" : "#000000",
              color: isDark ? "#000000" : "#FFFFFF",
            }}
            data-testid="auth-option-apple"
          >
            <span
              className="absolute flex items-center justify-center"
              style={{ left: 22, width: 24 }}
            >
              <AppleLogo size={22} color={isDark ? "#000000" : "#FFFFFF"} />
            </span>
            <span
              className="font-bold"
              style={{ fontSize: 17, letterSpacing: "-0.01em" }}
            >
              Continue with Apple
            </span>
          </button>

          {/* Google — secondary surface */}
          <button
            type="button"
            onClick={() => notReady("Google")}
            className={secondaryBtnClass}
            data-testid="auth-option-google"
          >
            <span
              className="absolute flex items-center justify-center"
              style={{ left: 22, width: 24 }}
            >
              <GoogleLogo size={21} />
            </span>
            <span
              className="font-bold text-foreground"
              style={{ fontSize: 17, letterSpacing: "-0.01em" }}
            >
              Continue with Google
            </span>
          </button>

          {/* Email — routes to the existing signup / login screens */}
          <button
            type="button"
            onClick={goEmail}
            className={secondaryBtnClass}
            data-testid="auth-option-email"
          >
            <span
              className="absolute flex items-center justify-center"
              style={{ left: 22, width: 24 }}
            >
              <Mail size={21} strokeWidth={2} className="text-foreground" />
            </span>
            <span
              className="font-bold text-foreground"
              style={{ fontSize: 17, letterSpacing: "-0.01em" }}
            >
              Continue with Email
            </span>
          </button>
        </div>

        {/* Inline notice for Apple/Google. Mirrors iOS "existsNotice"/error
            styling — soft rounded box, subtle tinted background, brand border. */}
        {notice ? (
          <div
            className="rounded-2xl border px-3.5 py-3 text-center"
            style={{
              marginTop: 16,
              borderColor: isDark ? "#9B6BFF" : "#7638FA",
              backgroundColor: "rgba(118, 56, 250, 0.08)",
            }}
            data-testid="auth-options-notice"
          >
            <span className="text-[13.5px] font-semibold text-foreground/90">
              {notice}
            </span>
          </div>
        ) : null}
      </div>
    </div>
  );
}
